import { useEffect, useState } from "react";
import {
  Box, Typography, Button, TextField, InputAdornment,
  IconButton, Alert, CircularProgress, LinearProgress,
} from "@mui/material";
import Visibility       from "@mui/icons-material/Visibility";
import VisibilityOff    from "@mui/icons-material/VisibilityOff";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockResetIcon    from "@mui/icons-material/LockReset";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useNavigate, useSearchParams } from "react-router-dom";
import logo from "../asserts/logo/Logo.svg";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

function getStrength(pwd) {
  let score = 0;
  if (pwd.length >= 8)           score++;
  if (/[A-Z]/.test(pwd))        score++;
  if (/[0-9]/.test(pwd))        score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return score;
}

const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"];
const strengthColor = ["", "#EF4444", "#F59E0B", "#3B82F6", "#10B981"];

function ChangePasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();

  // Prefer URL token; fall back to sessionStorage stash (keycloak.js may have cleaned ?realm=)
  const tokenFromUrl = (searchParams.get("token") || "").trim();
  let tokenFromStore = "";
  try {
    tokenFromStore = (sessionStorage.getItem("pwd_reset_token") || "").trim();
  } catch {
    tokenFromStore = "";
  }
  const token = tokenFromUrl || tokenFromStore;
  const realmFromQuery = searchParams.get("realm") || "";
  const tokenMissing = !token;

  const [username, setUsername] = useState("");
  const [isFirstLoginSetup, setIsFirstLoginSetup] = useState(false);
  const [validating, setValidating] = useState(!tokenMissing);
  const [tokenInvalid, setTokenInvalid] = useState(tokenMissing);

  const [newPassword,     setNewPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew,         setShowNew]         = useState(false);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [error,           setError]           = useState(
    tokenMissing
      ? "Reset link has expired or is invalid. Please request a new one."
      : ""
  );
  const [loading,         setLoading]         = useState(false);

  const isForgotReset = !isFirstLoginSetup;
  const strength    = getStrength(newPassword);
  const strengthPct = (strength / 4) * 100;
  const formBlocked = tokenMissing || tokenInvalid || validating;

  useEffect(() => {
    if (realmFromQuery) {
      localStorage.setItem("kc_realm", realmFromQuery.toLowerCase());
    }
  }, [realmFromQuery]);

  useEffect(() => {
    if (tokenMissing) {
      setValidating(false);
      setTokenInvalid(true);
      return;
    }

    let cancelled = false;
    (async () => {
      setValidating(true);
      try {
        const res = await fetch(`${BACKEND}/api/validate-reset-token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!res.ok || !data.valid) {
          setTokenInvalid(true);
          setError(data.message || "Reset link has expired or is invalid. Please request a new one.");
          return;
        }

        setTokenInvalid(false);
        setUsername(data.username || "");
        setIsFirstLoginSetup(data.purpose === "first_login_reset");
        if (data.realm) {
          localStorage.setItem("kc_realm", String(data.realm).toLowerCase());
        }
        setError("");
      } catch {
        if (!cancelled) {
          setTokenInvalid(true);
          setError("Unable to validate reset link. Please try again.");
        }
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();

    return () => { cancelled = true; };
  }, [token, tokenMissing]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formBlocked || loading) {
      setError("Reset link has expired or is invalid. Please request a new one.");
      return;
    }
    if (newPassword.length < 8)         { setError("Password must be at least 8 characters."); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    if (strength < 2)                   { setError("Password too weak. Add uppercase, numbers or symbols."); return; }

    setLoading(true);
    try {
      const res  = await fetch(`${BACKEND}/api/change-password`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await res.json();
      if (!res.ok) {
        if (data.code === "TOKEN_INVALID" || /expired|invalid|new link|new reset/i.test(data.message || "")) {
          setTokenInvalid(true);
          setError(data.message || "Reset link has expired or is invalid. Please request a new one.");
        } else {
          setError(data.message || "Failed to change password.");
        }
        return;
      }

      try { sessionStorage.removeItem("pwd_reset_token"); } catch { /* ignore */ }

      // Always return to Login after password set (forgot + first-time invite).
      navigate("/", {
        replace: true,
        state: {
          loginMessage: isFirstLoginSetup
            ? "Your password is set. Please sign in with your new password."
            : (data.message ||
              "Password updated successfully. Please log in with your new password."),
        },
      });

    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      display: "flex",
      width: "100vw",
      height: "100vh",
      overflow: "hidden",
      bgcolor: "#FFFFFF",
    }}>

      <Box sx={{
        width: { xs: "0px", md: "480px" },
        minWidth: { xs: "0px", md: "480px" },
        height: "100%",
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        justifyContent: "space-between",
        background: "linear-gradient(180deg, #001233 0%, #002266 35%, #003399 70%, #0046B8 100%)",
        padding: "40px 48px",
        position: "relative",
        overflow: "hidden",
      }}>
        <Box sx={{
          position: "absolute", width: "600px", height: "600px", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0,70,184,0.5) 0%, transparent 65%)",
          top: "-200px", right: "-200px", pointerEvents: "none",
        }} />
        <Box sx={{
          position: "absolute", width: "500px", height: "500px", borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0,51,153,0.4) 0%, transparent 65%)",
          bottom: "-100px", left: "-150px", pointerEvents: "none",
        }} />

        <Box component="img" src={logo}
          sx={{ height: 44, objectFit: "contain", objectPosition: "left", filter: "brightness(0) invert(1)", zIndex: 1 }}
        />

        <Box sx={{ zIndex: 1 }}>
          <Typography sx={{
            fontSize: { md: "28px", lg: "36px" },
            fontWeight: 800, color: "#FFFFFF", lineHeight: 1.25, mb: 2,
          }}>
            Govern AI.<br />
            Ensure Compliance.<br />
            <Box component="span" sx={{ color: "#38BDF8" }}>Build Trust.</Box>
          </Typography>
          <Typography sx={{
            fontSize: "13px", color: "rgba(255,255,255,0.55)",
            lineHeight: 1.7, maxWidth: "320px",
          }}>
            The enterprise-grade platform for validating, monitoring,
            and governing AI systems across your organization.
          </Typography>
        </Box>

        <Typography sx={{ fontSize: "11px", color: "rgba(255,255,255,0.3)", zIndex: 1 }}>
          © QualiZeal 2026. All Rights Reserved
        </Typography>
      </Box>

      <Box sx={{
        flex: 1,
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#FFFFFF",
        overflowY: "auto",
        padding: { xs: "24px", md: "40px" },
      }}>

        <Box sx={{
          width: "100%",
          maxWidth: "640px",
          bgcolor: "#FFFFFF",
          borderRadius: "12px",
          border: "1px solid #E5E7EB",
          boxShadow: "0px 8px 32px rgba(0,0,0,0.08)",
          padding: { xs: "32px 24px", md: "48px 64px" },
        }}>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: "10px",
              bgcolor: formBlocked && !validating ? "#FEF2F2" : "#EFF6FF",
              display: "flex",
              alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              {formBlocked && !validating ? (
                <ErrorOutlineIcon sx={{ color: "#DC2626", fontSize: 22 }} />
              ) : (
                <LockResetIcon sx={{ color: "#1D4ED8", fontSize: 22 }} />
              )}
            </Box>
            <Typography sx={{ fontSize: "24px", fontWeight: 700, color: "#111827" }}>
              {validating
                ? "Reset Password"
                : formBlocked
                  ? "Link expired"
                  : (isFirstLoginSetup ? "Set Up Your Password" : "Reset Password")}
            </Typography>
          </Box>

          {validating ? (
            <>
              <Typography sx={{ color: "#6B7280", fontSize: "13px", mb: 3 }}>
                Checking your reset link…
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            </>
          ) : formBlocked ? (
            <>
              <Typography sx={{ color: "#6B7280", fontSize: "13px", mb: 3 }}>
                This reset link is no longer valid. It may have expired, already been used,
                or been replaced by a newer request.
              </Typography>
              <Alert severity="error" sx={{ mb: 3, fontSize: "13px", borderRadius: "8px" }}>
                {error || "Reset link has expired or is invalid. Please request a new one."}
              </Alert>
              <Button
                variant="contained"
                fullWidth
                onClick={() => navigate("/")}
                sx={{
                  bgcolor: "#1D4ED8", borderRadius: "6px", textTransform: "none",
                  py: 1.4, fontSize: "15px", fontWeight: 600, boxShadow: "none",
                  "&:hover": { bgcolor: "#1E40AF" },
                }}
              >
                Back to Login
              </Button>
              <Typography sx={{ textAlign: "center", fontSize: "13px", color: "#9CA3AF", mt: 2 }}>
                From Login, use Forgot Password to get a new link.
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ color: "#6B7280", fontSize: "13px", mb: isFirstLoginSetup ? 1.5 : 3 }}>
                {isForgotReset ? (
                  <>
                    {username ? (
                      <>Reset the password for <strong style={{ color: "#111827" }}>{username}</strong>.</>
                    ) : (
                      <>Choose a new password for your account.</>
                    )}
                    {" "}This link expires in 15 minutes.
                  </>
                ) : (
                  <>
                    Hi{" "}
                    <strong style={{ color: "#111827" }}>{username || "there"}</strong>
                    — your account still needs its initial password. Choose a permanent password
                    to finish setup, then sign in with it.
                  </>
                )}
              </Typography>

              {isFirstLoginSetup && (
                <Alert severity="info" sx={{ mb: 3, fontSize: "13px", borderRadius: "8px" }}>
                  This is your first-time password setup. The temporary password from your invite
                  can no longer be used after you set a new one.
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                {error && (
                  <Alert severity="error" sx={{ mb: 2, fontSize: "13px", borderRadius: "8px" }}>
                    {error}
                  </Alert>
                )}

                <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#374151", mb: 0.5 }}>
                  *New Password
                </Typography>
                <TextField fullWidth size="small"
                  type={showNew ? "text" : "password"}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(""); }}
                  sx={{ mb: 0.5 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowNew(!showNew)} edge="end">
                          {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: "6px", fontSize: "14px", bgcolor: "#FAFAFA" },
                  }}
                />

                {newPassword && (
                  <Box sx={{ mb: 2 }}>
                    <LinearProgress variant="determinate" value={strengthPct}
                      sx={{
                        height: 4, borderRadius: 2, bgcolor: "#F3F4F6",
                        "& .MuiLinearProgress-bar": { bgcolor: strengthColor[strength], borderRadius: 2 },
                      }}
                    />
                    <Typography sx={{ fontSize: "11px", color: strengthColor[strength], mt: 0.5, textAlign: "right" }}>
                      {strengthLabel[strength]}
                    </Typography>
                  </Box>
                )}

                <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#374151", mb: 0.5, mt: newPassword ? 0 : 2 }}>
                  *Confirm Password
                </Typography>
                <TextField fullWidth size="small"
                  type={showConfirm ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                  sx={{ mb: 1.5 }}
                  error={!!confirmPassword && confirmPassword !== newPassword}
                  helperText={confirmPassword && confirmPassword !== newPassword ? "Passwords do not match" : ""}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: 18, color: "#9CA3AF" }} /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowConfirm(!showConfirm)} edge="end">
                          {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { borderRadius: "6px", fontSize: "14px", bgcolor: "#FAFAFA" },
                  }}
                />

                <Box sx={{ mb: 3 }}>
                  {[
                    { label: "At least 8 characters", ok: newPassword.length >= 8 },
                    { label: "One uppercase letter",   ok: /[A-Z]/.test(newPassword) },
                    { label: "One number",             ok: /[0-9]/.test(newPassword) },
                    { label: "One special character",  ok: /[^A-Za-z0-9]/.test(newPassword) },
                  ].map(({ label, ok }) => (
                    <Typography key={label} sx={{
                      fontSize: "12px",
                      color: ok ? "#10B981" : "#9CA3AF",
                      display: "flex", alignItems: "center", gap: 0.5,
                      lineHeight: 1.8,
                    }}>
                      {ok ? "✓" : "○"} {label}
                    </Typography>
                  ))}
                </Box>

                <Button type="submit" variant="contained" fullWidth disabled={loading}
                  sx={{
                    bgcolor: "#1D4ED8", borderRadius: "6px", textTransform: "none",
                    py: 1.4, fontSize: "15px", fontWeight: 600, boxShadow: "none",
                    "&:hover": { bgcolor: "#1E40AF" },
                  }}
                >
                  {loading
                    ? <CircularProgress size={20} color="inherit" />
                    : (isFirstLoginSetup ? "Save Password & Go to Login" : "Reset Password")}
                </Button>
              </form>

              <Typography sx={{ textAlign: "center", fontSize: "13px", color: "#9CA3AF", mt: 2.5 }}>
                {isFirstLoginSetup ? "Need to start over? " : "Remember your password? "}
                <Typography component="span"
                  onClick={() => navigate("/")}
                  sx={{ color: "#1D4ED8", fontWeight: 600, cursor: "pointer", fontSize: "13px" }}>
                  Back to Login
                </Typography>
              </Typography>
            </>
          )}

        </Box>
      </Box>
    </Box>
  );
}

export default ChangePasswordPage;
